
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import SignupForm from '@/components/SignupForm';
import DynamicSignupForm from '@/components/DynamicSignupForm';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useReferralSignup } from '@/hooks/useReferralSignup';
import { getSignupForm } from '@/utils/config/signupForm';
import { SignupFormConfig } from '@/schemas/signupFormSchema';
import { EnhancedSignupData } from '@/types/userSignup';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SignupPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [formConfig, setFormConfig] = useState<SignupFormConfig | null>(null);
  const [fraudDetected, setFraudDetected] = useState(false);
  const [fraudReasons, setFraudReasons] = useState<string[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { processReferralSignup, isProcessing } = useReferralSignup();

  // Load dynamic form configuration if available
  useEffect(() => {
    const loadFormConfig = async () => {
      try {
        const config = await getSignupForm();
        if (config) {
          setFormConfig(config);
        }
      } catch (error) {
        console.error('Error loading signup form configuration:', error);
      } finally {
        setIsFormLoading(false);
      }
    };
    
    loadFormConfig();
  }, []);

  const handleSubmit = async (formData: EnhancedSignupData) => {
    setIsLoading(true);
    
    // Check for fraud
    if (formData.fraudCheck?.isFraud) {
      setFraudDetected(true);
      setFraudReasons(formData.fraudCheck.reasons);
      setIsLoading(false);
      
      toast({
        title: 'Signup Blocked',
        description: 'Potential fraud detected. Please review your information.',
        variant: 'destructive',
      });
      
      return;
    }
    
    // Reset fraud state if previously set
    setFraudDetected(false);
    setFraudReasons([]);
    
    try {
      // Process the referral signup
      const result = await processReferralSignup({
        ...formData,
        geoData: formData.geoData // Pass geo data to backend
      });
      
      if (result.success) {
        toast({
          title: 'Account Created',
          description: result.message || 'Please check your email to activate your account.',
        });
        
        // Navigate to a confirmation page
        navigate('/signup-confirmation', { 
          state: { 
            email: formData.email,
            name: formData.name
          } 
        });
      } else {
        toast({
          title: 'Signup Failed',
          description: result.message || 'There was a problem creating your account',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast({
        title: 'Signup Failed',
        description: 'There was a problem creating your account',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-primary-blue">Join Our Referral Program</h1>
          <p className="mt-2 text-gray-600">Create your referral account to get started</p>
        </div>
        
        {fraudDetected && (
          <Alert variant="destructive" className="mb-4">
            <AlertTitle className="flex items-center">
              Fraud Detection Alert
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <HelpCircle className="h-4 w-4 ml-2 cursor-help" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-semibold">Reasons for fraud detection:</p>
                    <ul className="list-disc pl-4 text-xs mt-1">
                      {fraudReasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </AlertTitle>
            <AlertDescription>
              Your submission has been flagged for potential fraud. Please check the provided information or contact support.
            </AlertDescription>
          </Alert>
        )}
        
        <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-2xl font-bold text-center">Create a Referral Account</CardTitle>
            <CardDescription className="text-center">Sign up for our referral program</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {isFormLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : formConfig ? (
              <DynamicSignupForm 
                onSubmit={handleSubmit} 
                isLoading={isLoading || isProcessing}
                formConfig={formConfig}
              />
            ) : (
              <SignupForm 
                onSubmit={handleSubmit} 
                isLoading={isLoading || isProcessing} 
              />
            )}
          </CardContent>
        </Card>
        
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <a 
              href="/login" 
              className="font-medium text-primary hover:text-primary/90 transition-colors"
            >
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
